import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SidePanel from "./sidePanel";
import { redirectToWhatsapp } from "../utils/redirectToWhatsapp";
import CUSTOMERS from "../api/services/CUSTOMERS";
import PLANS from "../api/services/PLANS";
import { paths } from "../routes/paths";
import styles from "../styles/_components/clientDetailsModal.module.scss";

const ClientDetailsModal = ({ userId, customerName, phone, setIsOpen, setSuccess }) => {
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [plans, setPlans] = useState([]);

  // Client details (editable)
  const [clientData, setClientData] = useState({
    name: customerName || "",
    phone: phone || "",
    email: "",
    idNumber: "",
    address: "",
    dateOfBirth: "",
    gender: "",
  });

  // Inline editing state
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");

  // Fetch client details
  const fetchClient = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await CUSTOMERS.get_by_id(userId);
      const client = res?.data || res;
      setClientData({
        name: client.name || customerName || "",
        phone: client.phone || phone || "",
        email: client.email || "",
        idNumber: client.idNumber || "",
        address: client.address || "",
        dateOfBirth: client.dateOfBirth || "",
        gender: client.gender || "",
      });
    } catch (error) {
      console.log("Error fetching client:", error);
    }
  }, [userId]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!userId) return;
    setStatsLoading(true);
    try {
      const res = await CUSTOMERS.get_stats(userId);
      setStats(res?.data || res);
    } catch (error) {
      console.log("Error fetching stats:", error);
    }
    setStatsLoading(false);
  }, [userId]);

  // Fetch subscription plans
  const fetchPlans = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await PLANS.get_user_plans({ userId });
      const data = res?.data?.data || res?.data || [];
      setPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      console.log("Error fetching plans:", err);
    }
  }, [userId]);

  useEffect(() => {
    fetchClient();
    fetchStats();
    fetchPlans();
  }, [fetchClient, fetchStats, fetchPlans]);

  // Start inline edit
  const startEdit = (field) => {
    setEditingField(field);
    setEditValue(clientData[field] || "");
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingField(null);
    setEditValue("");
  };

  // Save edit — update locally immediately, sync to server
  const saveEdit = async () => {
    if (!editingField) return;
    const updated = { ...clientData, [editingField]: editValue };
    setClientData(updated);
    setEditingField(null);
    setEditValue("");

    try {
      await CUSTOMERS.create_client({
        userId,
        name: updated.name,
        phone: updated.phone,
        email: updated.email,
      });
      toast.success("פרטי הלקוח עודכנו");
      if (setSuccess) setSuccess(true);
    } catch (error) {
      console.log("Error updating client:", error);
      toast.error("שגיאה בעדכון פרטים");
    }
  };

  // Handle Enter key in edit input
  const handleEditKeyDown = (e) => {
    if (e.key === "Enter") saveEdit();
    if (e.key === "Escape") cancelEdit();
  };

  const getFirstLetter = (name) => {
    if (!name) return "?";
    const first = name.charAt(0);
    if (/[A-Za-z\u0590-\u05FF]/.test(first)) return first.toUpperCase();
    return "?";
  };

  const genderOptions = [
    { value: "male", label: "זכר" },
    { value: "female", label: "נקבה" },
    { value: "other", label: "אחר" },
  ];

  const genderLabel = (g) => genderOptions.find((o) => o.value === g)?.label || "לא צוין";

  const formatDateDisplay = (d) => {
    if (!d) return "לא צוין";
    try { return new Date(d).toLocaleDateString("he-IL"); }
    catch { return "לא צוין"; }
  };

  const formatDateInput = (d) => {
    if (!d) return "";
    try {
      const dt = new Date(d);
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    } catch { return ""; }
  };

  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Track changes
  const updateField = (key, value) => {
    setClientData((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  // Save all changes
  const saveAllChanges = async () => {
    setSaving(true);
    try {
      await CUSTOMERS.create_client({
        userId,
        name: clientData.name,
        phone: clientData.phone,
        email: clientData.email,
        idNumber: clientData.idNumber,
        address: clientData.address,
        dateOfBirth: clientData.dateOfBirth,
        gender: clientData.gender,
      });
      toast.success("פרטי הלקוח נשמרו");
      setHasChanges(false);
      if (setSuccess) setSuccess(true);
    } catch (err) {
      console.log(err);
      toast.error("שגיאה בשמירת פרטים");
    }
    setSaving(false);
  };

  return (
    <SidePanel setIsOpen={setIsOpen} title="פרטי לקוח">
      <div className={styles.container}>

        {/* ── Header: Avatar + Name + Phone + Quick Actions ── */}
        <div className={styles.header}>
          <div className={styles.avatar}>
            {getFirstLetter(clientData.name)}
          </div>
          <div className={styles.headerInfo}>
            <div className={styles.clientName}>{clientData.name || "לקוח"}</div>
            {clientData.phone && (
              <div className={styles.clientPhone}>{clientData.phone}</div>
            )}
          </div>
          <div className={styles.quickActions}>
            {clientData.phone && (
              <>
                <a
                  href={`tel:${clientData.phone}`}
                  className={styles.quickActionBtn}
                  title="התקשר"
                >
                  <img src="/assets/phone-icon-1.svg" alt="call" />
                </a>
                <button
                  className={`${styles.quickActionBtn} ${styles.quickActionBtnGreen}`}
                  onClick={() => redirectToWhatsapp(clientData.phone)}
                  title="WhatsApp"
                >
                  <img src="/assets/whatsapp-icon-1.svg" alt="whatsapp" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Personal Details Card ── */}
        <div className={styles.card}>
          <div className={styles.cardTitle}>פרטים אישיים</div>
          <div className={styles.fieldsGrid}>
            {/* Row 1: Name + Phone */}
            <div className={styles.fieldItem}>
              <label className={styles.fieldLabel}>שם</label>
              <input
                className={styles.fieldInput}
                value={clientData.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="שם מלא"
              />
            </div>
            <div className={styles.fieldItem}>
              <label className={styles.fieldLabel}>טלפון</label>
              <input
                className={styles.fieldInput}
                value={clientData.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                placeholder="050..."
                dir="ltr"
              />
            </div>

            {/* Row 2: Email + ID */}
            <div className={styles.fieldItem}>
              <label className={styles.fieldLabel}>אימייל</label>
              <input
                className={styles.fieldInput}
                value={clientData.email}
                onChange={(e) => updateField("email", e.target.value)}
                placeholder="email@example.com"
                dir="ltr"
              />
            </div>
            <div className={styles.fieldItem}>
              <label className={styles.fieldLabel}>ת.ז</label>
              <input
                className={styles.fieldInput}
                value={clientData.idNumber}
                onChange={(e) => updateField("idNumber", e.target.value)}
                placeholder="מספר זהות"
                dir="ltr"
              />
            </div>

            {/* Row 3: Address (full width) */}
            <div className={`${styles.fieldItem} ${styles.fieldFull}`}>
              <label className={styles.fieldLabel}>כתובת</label>
              <input
                className={styles.fieldInput}
                value={clientData.address}
                onChange={(e) => updateField("address", e.target.value)}
                placeholder="כתובת מלאה"
              />
            </div>

            {/* Row 4: Date of Birth + Gender */}
            <div className={styles.fieldItem}>
              <label className={styles.fieldLabel}>תאריך לידה</label>
              <input
                type="date"
                className={styles.fieldInput}
                value={formatDateInput(clientData.dateOfBirth)}
                onChange={(e) => updateField("dateOfBirth", e.target.value)}
              />
            </div>
            <div className={styles.fieldItem}>
              <label className={styles.fieldLabel}>מגדר</label>
              <select
                className={styles.fieldInput}
                value={clientData.gender || ""}
                onChange={(e) => updateField("gender", e.target.value)}
              >
                <option value="">לא צוין</option>
                {genderOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ── Statistics Card ── */}
        <div className={styles.card}>
          <div className={styles.cardTitle}>
            <span className={styles.cardTitleIcon}>
              <img src="/assets/chart.png" alt="" width={16} />
            </span>
            סטטיסטיקות
          </div>
          {statsLoading ? (
            <div className={styles.loadingState}>טוען נתונים...</div>
          ) : (
            <div className={styles.statsGrid}>
              <div className={styles.statItem}>
                <span className={`${styles.statValue} ${styles.statValueBrand}`}>
                  {stats?.totalAppointments ?? 0}
                </span>
                <span className={styles.statLabel}>סה"כ תורים</span>
              </div>
              <div className={styles.statItem}>
                <span className={`${styles.statValue} ${styles.statValueGreen}`}>
                  {stats?.completedAppointments ?? 0}
                </span>
                <span className={styles.statLabel}>הושלמו</span>
              </div>
              <div className={styles.statItem}>
                <span className={`${styles.statValue} ${styles.statValueRed}`}>
                  {stats?.cancelledAppointments ?? 0}
                </span>
                <span className={styles.statLabel}>בוטלו</span>
              </div>
            </div>
          )}
        </div>

        {/* ── Save button ── */}
        {hasChanges && (
          <button
            className={styles.saveBtn}
            onClick={saveAllChanges}
            disabled={saving}
          >
            {saving ? "שומר..." : "שמירת שינויים"}
          </button>
        )}

        {/* ── Subscription Plans ── */}
        {plans.length > 0 && (
          <div className={styles.card}>
            <div className={styles.cardTitle}>מנויים וכרטיסיות</div>
            <div className={styles.plansList}>
              {plans.map((plan, idx) => {
                const name = plan.servicePlanId?.name || plan.name || "מנוי";
                const remaining = plan.oneTimeTurns != null
                  ? plan.oneTimeTurns - (plan.consumedOneTimeTurns || 0)
                  : null;
                const isExpired = plan.endDate && new Date(plan.endDate) < new Date();
                const isFrozen = plan.status === "FROZEN";
                return (
                  <div key={plan._id || idx} className={styles.planRow}>
                    <div className={styles.planInfo}>
                      <span className={styles.planName}>{name}</span>
                      {remaining != null && (
                        <span className={styles.planRemaining}>
                          {remaining} כניסות נותרו
                        </span>
                      )}
                    </div>
                    <span
                      className={`${styles.planBadge} ${
                        isExpired
                          ? styles.planBadgeRed
                          : isFrozen
                          ? styles.planBadgeAmber
                          : styles.planBadgeGreen
                      }`}
                    >
                      {isExpired ? "פג תוקף" : isFrozen ? "מוקפא" : "פעיל"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Action Menu ── */}
        <div className={styles.actions}>
          <button
            className={styles.actionBtn}
            onClick={() => {
              navigate(paths.clientMemberShip(userId));
              setIsOpen(false);
            }}
          >
            <img src="/assets/card-icon-1.svg" alt="card" />
            <span>כרטיסיות ומנויים</span>
          </button>

          <button
            className={styles.actionBtn}
            onClick={() => {
              navigate(paths.clientHistory(userId));
              setIsOpen(false);
            }}
          >
            <img src="/assets/history-icon-1.svg" alt="history" />
            <span>היסטוריית תורים</span>
          </button>

          <button
            className={styles.actionBtn}
            onClick={() => {
              navigate(paths.clientBilling(userId));
              setIsOpen(false);
            }}
          >
            <img src="/assets/books-icon-1.svg" alt="billing" />
            <span>הנהלת חשבונות</span>
          </button>

          <button
            className={`${styles.actionBtn} ${styles.actionBtnBrand}`}
            onClick={() => {
              navigate(paths.createAppointment, { state: { clientId: userId, clientName: clientData.name, clientPhone: clientData.phone } });
              setIsOpen(false);
            }}
          >
            <img src="/assets/add-icon-1.svg" alt="add" />
            <span>קביעת תור</span>
          </button>
        </div>

      </div>
    </SidePanel>
  );
};

export default ClientDetailsModal;
